import * as React from 'react';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import List from 'antd/lib/list';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { useDispatch } from 'react-redux';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { isIosPlatform } from '../../helpers';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { AppPlatform } from '../../types';
import { runwayColors } from '../styles/globalStyles';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface FilterPathsModalProps extends AppComponentProps {
    open: boolean;
    initialPaths: string[];
    onCancel: () => void;
    platform: AppPlatform;
}

const FilterPathsModal: React.FC<FilterPathsModalProps> = (props: FilterPathsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [pathNames, setPathNames] = React.useState(props.initialPaths ?? []);

    const editFilterPathsIsLoading = useIsLoading(AppSettingsUpdatable.UpdateFilterPaths);
    const editFilterPathsError = useError(AppSettingsUpdatable.UpdateFilterPaths);
    const editFilterPathsSuccess = useUpdated(AppSettingsUpdatable.UpdateFilterPaths);

    const saveFilterPaths = (newFilePaths) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateFilterPaths, {
                orgId: props.orgId,
                appId: props.appId,
                filterPaths: newFilePaths,
            }),
        );
    };

    React.useEffect(() => {
        if (editFilterPathsError) {
            globalMessage.error(editFilterPathsError);
        }
    }, [editFilterPathsError]);

    React.useEffect(() => {
        if (editFilterPathsSuccess) {
            globalMessage.success('Included paths updated!');

            props.onCancel();
        }
    }, [editFilterPathsSuccess]);

    const fieldPlaceholder = isIosPlatform(props.platform) ? '/ios' : '/android';

    return (
        <Modal
            title="Edit included file paths"
            open={props.open}
            onOk={() => saveFilterPaths(pathNames)}
            onCancel={props.onCancel}
            okButtonProps={{ loading: editFilterPathsIsLoading }}
            afterClose={() => {
                setPathNames(props.initialPaths);
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>
                    Runway will filter out commits & PRs that affect code outside of the specified path(s). The filter
                    will apply for items displayed in the feature readiness step.
                </Text>

                <Form
                    form={form}
                    style={{
                        display: 'flex',
                        marginTop: 24,
                    }}
                    onFinish={(values) => {
                        const newPathName = values.pathname;
                        if (newPathName) {
                            setPathNames((prev) => [...prev, newPathName]);
                            form.resetFields();
                        }
                    }}
                >
                    <Form.Item
                        style={{ marginRight: 12, flex: 1 }}
                        name="pathname"
                        rules={[{ required: true, message: 'Please enter a file path.' }]}
                    >
                        <Input placeholder={fieldPlaceholder} autoFocus allowClear />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                        Add
                    </Button>
                </Form>

                <List
                    style={{ backgroundColor: runwayColors.white }}
                    bordered
                    size="small"
                    locale={{ emptyText: 'No paths added yet' }}
                    dataSource={pathNames}
                    renderItem={(pathName, index) => (
                        <List.Item key={index}>
                            <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        flex: 1,
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {pathName}
                                </div>
                                <Button
                                    danger
                                    size="small"
                                    key={index}
                                    icon={<DeleteOutlined />}
                                    type="link"
                                    onClick={() =>
                                        setPathNames((prev) => {
                                            const newFilenames = [...prev];
                                            newFilenames.splice(index, 1);
                                            return newFilenames;
                                        })
                                    }
                                />
                            </div>
                        </List.Item>
                    )}
                />
            </div>
        </Modal>
    );
};

export default FilterPathsModal;
